import { Button, FormControl, FormHelperText, FormLabel, Input, Stack } from '@mui/joy';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';

import OrientationIcon from '@/assets/icons/orientation-arrow.svg?react';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { EditModalDialog } from '@/components/signs/modal/EditModalDialog';
import { I18N_DE } from '@/i18n/de';
import { CreateSignGroupDto, SignGroupInstallationSide, SignGroupType } from '@/modules/api';
import { backendApiSignGroupsCreateMutation } from '@/modules/api/@tanstack/react-query.gen';
import { useSignGroupQuery } from '@/queries/useSignGroupQuery';
import { useSignSupportQuery } from '@/queries/useSignSupportQuery';
import { useCreateSignStore } from '@/stores/useCreateSignStore';

export const Route = createFileRoute(
  '/_authenticated/_management/verkehrszeichen/$adminLevelId/$signSupportId_/$signGroupId/duplizieren',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: DuplicateSignGroupRoute,
});

function DuplicateSignGroupRoute() {
  const formId = useId();
  const navigate = useNavigate();

  const { data: support } = useSignSupportQuery();
  const { data: group } = useSignGroupQuery();

  const { adminLevelId, signSupportId } = Route.useParams();

  const { resetStore, reloadSupportLayer } = useCreateSignStore((state) => state.actions);

  const {
    control,
    watch,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<Pick<CreateSignGroupDto, 'type' | 'bearing' | 'installationSide' | 'roadsideDistance'>>({
    defaultValues: { ...group },
  });

  const currentBearing = watch('bearing');

  const { mutate, error, isPending } = useMutation({
    ...backendApiSignGroupsCreateMutation(),
    onSuccess: ({ id }) => {
      resetStore();
      reloadSupportLayer();
      navigate({
        to: '/verkehrszeichen/$adminLevelId/$signSupportId/$signGroupId',
        params: {
          adminLevelId,
          signSupportId,
          signGroupId: id,
        },
      });
    },
  });

  return (
    support && (
      <EditModalDialog title="Neue Verkehrszeichen-Gruppe hinzufügen">
        <Stack
          component="form"
          id={formId}
          onSubmit={handleSubmit(({ type, bearing, installationSide, roadsideDistance }) => {
            if (isPending) return;

            mutate({
              body: {
                type,
                supportId: support.id,
                order: support.groups?.length || 0,
                installationSide,
                ...(bearing && { bearing }),
                ...(roadsideDistance && {
                  roadsideDistance: roadsideDistance * 100,
                }),
                signs: group?.signs?.map((sign) => ({
                  code: sign.code,
                  order: sign.order,
                  size: sign.size,
                  reflectionClass: sign.reflectionClass,
                  source: sign.source,
                  value: sign.value,
                  permanency: sign.permanency,
                })),
              },
            });
          })}
          gap={2}
        >
          <FormControl error={!!errors.type}>
            <FormLabel>Typ</FormLabel>
            <Controller
              control={control}
              name="type"
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  orientation="horizontal"
                  options={Object.values(SignGroupType)}
                  optionLabel={({ option }) => I18N_DE.SignGroupType[option]}
                />
              )}
            />
          </FormControl>

          <FormControl error={!!errors.installationSide}>
            <FormLabel>Aufstellungsseite</FormLabel>
            <Controller
              control={control}
              name="installationSide"
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  orientation="horizontal"
                  options={Object.values(SignGroupInstallationSide)}
                  optionLabel={({ option }) => I18N_DE.SignGroupInstallationSide[option]}
                />
              )}
            />
          </FormControl>

          <FormControl error={!!errors.bearing}>
            <FormLabel>Abstand zum Fahrbahnrand</FormLabel>
            <Stack direction="row">
              <Input
                type="number"
                variant="outlined"
                sx={{ width: 150 }}
                slotProps={{
                  input: {
                    ...register('roadsideDistance'),
                    label: 'optional',
                    step: 0.1,
                  },
                }}
                endDecorator="Meter"
              />
            </Stack>
          </FormControl>

          <FormControl error={!!errors.bearing}>
            <FormLabel>Ausrichtung</FormLabel>
            <Stack direction="row">
              <Input
                type="number"
                variant="outlined"
                sx={{ width: 150 }}
                slotProps={{
                  input: {
                    ...register('bearing'),
                    label: 'optional',
                    min: 0,
                    max: 359,
                    step: 1,
                  },
                }}
                endDecorator="° Grad"
              />
              {currentBearing && (
                <OrientationIcon
                  style={{
                    transform: `rotate(${currentBearing}deg)`,
                    width: 30,
                    height: 30,
                    margin: 'auto 20px',
                  }}
                />
              )}
            </Stack>
          </FormControl>
          {error && <FormHelperText>{error.message}</FormHelperText>}

          <Button type="submit" form={formId} loading={isPending} disabled={isPending}>
            Speichern
          </Button>
        </Stack>
      </EditModalDialog>
    )
  );
}
